/*
=========================================================
* RingFoods Features Section
=========================================================
*/

// @mui material components
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Chip from "@mui/material/Chip";

// @mui icons
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import TranslateIcon from "@mui/icons-material/Translate";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import TableRestaurantIcon from "@mui/icons-material/TableRestaurant";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import SmartToyIcon from "@mui/icons-material/SmartToy";
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';

// Material Kit 2 PRO React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import MKButton from "components/MKButton";

import { brandSoftGradient, brandPrimary, grey100 } from "pages/RingFoodsHomepage/theme";
import useSignupUrl from "hooks/useSignupUrl";
import { getDisplayTrialDays } from "constants/promo";

function Features() {
  const signupUrl = useSignupUrl();
  const features = [
    {
      icon: AccessTimeIcon,
      title: "Your Team Gets Their Nights Back",
      description:
        "No more 2am calls to your personal cell. RingFoods answers every after-hours call, books reservations, and handles questions -- so you actually sleep.",
      status: "active",
    },
    {
      icon: CalendarTodayIcon,
      title: "Tables Fill While You Sleep",
      description:
        "Real-time Google Calendar sync means callers get instant confirmation. No hold music, no missed bookings, no morning voicemail regrets.",
      status: "active",
    },
    {
      icon: TableRestaurantIcon,
      title: "No More Double-Booking Headaches",
      description:
        "AI knows your tables, your rules, and your capacity. It automatically optimizes seating, joins tables for large parties, and prevents conflicts before they happen.",
      status: "active",
    },
    {
      icon: IntegrationInstructionsIcon,
      title: "Orders Flow Straight to Your Kitchen",
      description:
        "Works seamlessly with Square, Clover, Toast, and Lightspeed. No phone tag, no misheard orders, no \"I thought you said pepperoni.\" Orders arrive accurate and ready. (Professional & Enterprise plans)",
      status: "active",
    },
    {
      icon: SmartToyIcon,
      title: "Set Up Today. Problem Solved Tomorrow.",
      description:
        "AI learns your restaurant's details instantly from your business information, hours, tables, and menus. 30 minutes of setup, no IT team needed, no training required.",
      status: "active",
    },
    {
      icon: TranslateIcon,
      title: "Speaks Every Customer's Language",
      description:
        "Auto-detects the caller's language and responds natively. Mandarin, Spanish, French, Arabic -- no more language barrier awkwardness, no more lost customers.",
      status: "active",
    },
  ];

  return (
    <MKBox component="section" py={{ xs: 8, md: 12 }} id="features">
      <Container>
        {/* Section Header */}
        <Grid container spacing={3} sx={{ mb: { xs: 6, md: 10 } }}>
          <Grid item xs={12} lg={6} mx="auto" textAlign="center">
            <MKTypography
              variant="h2"
              fontWeight="bold"
              color="primary"
              sx={{ mb: 2, fontSize: { xs: "1.85rem", sm: "2.25rem", md: "2.5rem" } }}
            >
              Built to Solve Real Restaurant Problems
            </MKTypography>
            <MKTypography variant="body1" color="text" sx={{ fontSize: { xs: "1rem", md: "1.125rem" }, mb: 2 }}>
              Every feature exists because a restaurant owner told us about a real problem. This is
              restaurant phone answering built around how a dining room actually runs &mdash;
              here&apos;s how RingFoods solves them.
            </MKTypography>
          </Grid>
        </Grid>

        {/* Features Grid */}
        <Grid container spacing={3}>
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Grid item xs={12} md={6} lg={4} key={index}>
                <Card
                  sx={{
                    height: "100%",
                    transition: "all 0.3s ease",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 15px 30px rgba(0,0,0,0.1)",
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: "center" }}>
                    {/* Icon */}
                    <MKBox
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: 2,
                        backgroundColor: "rgba(26, 26, 26, 0.08)",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mb: 2,
                      }}
                    >
                      <Icon sx={{ fontSize: 30, color: brandPrimary }} />
                    </MKBox>

                    {/* Title */}
                    <MKTypography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
                      {feature.title}
                    </MKTypography>

                    {/* Description */}
                    <MKTypography variant="body2" color="text" sx={{ opacity: 0.8 }}>
                      {feature.description}
                    </MKTypography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Bottom CTA */}
        <Grid container spacing={3} sx={{ mt: 8 }}>
          <Grid item xs={12} textAlign="center">
            <MKBox
              sx={{
                background: brandSoftGradient,
                borderRadius: 3,
                p: { xs: 3, md: 4 },
                border: "1px solid rgba(26, 26, 26, 0.1)",
              }}
            >
              <MKTypography variant="h4" fontWeight="bold" color="text" sx={{ mb: 2, fontSize: { xs: "1.4rem", md: "2.125rem" } }}>
                Your Staff Will Thank You
              </MKTypography>
              <MKTypography variant="body1" color="text" sx={{ mb: 3, opacity: 0.8, fontSize: { xs: "1rem", md: "1.125rem" } }}>
                Give your team the gift of never dealing with phone chaos again. Setup takes 30 minutes.
              </MKTypography>
              <Grid container spacing={2} justifyContent="center">
                <Grid item>
                  <Chip
                    label="&#10003; Setup in 30 minutes"
                    sx={{
                      backgroundColor: "rgba(26, 26, 26, 0.08)",
                      color: brandPrimary,
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label="&#10003; No long-term contracts"
                    sx={{
                      backgroundColor: "rgba(26, 26, 26, 0.08)",
                      color: brandPrimary,
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  />
                </Grid>
                <Grid item>
                  <Chip
                    label={`✓ ${getDisplayTrialDays()}-day free trial`}
                    sx={{
                      backgroundColor: "rgba(26, 26, 26, 0.08)",
                      color: brandPrimary,
                      fontSize: "0.875rem",
                      fontWeight: 600,
                    }}
                  />
                </Grid>
              </Grid>
              <MKBox mt={3}>
                <MKButton
                  variant="contained"
                  onClick={() => window.location.href = signupUrl}
                  sx={{
                    backgroundColor: "#1A1A1A !important",
                    color: "#FFFFFF !important",
                    fontWeight: 600,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: "#333333 !important",
                    },
                  }}
                >
                  Start Your Free Trial
                </MKButton>
              </MKBox>
            </MKBox>
          </Grid>
        </Grid>
      </Container>
    </MKBox>
  );
}

export default Features;
